import {
    type DragEvent,
    type FC,
    Fragment,
    type MouseEvent,
    useRef,
    useState,
} from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import {
    type FileConnectionType,
    getFileType,
    MEDIA_DRAG_MIME,
    MediaItemType,
    type MediaSelectedType,
    MediaType,
} from "./MediaUtilities";

interface Props {
    id: string;
    name: string;
    type: MediaItemType;
    index: number;
    thumbnail?: string;
    extension?: string;
    selected: boolean;
    onClick: (event: MouseEvent, index: number, item: MediaSelectedType) => void;
    navUp?: boolean;
    newFolder?: boolean;
    hasChildren?: boolean;
    connections: Array<FileConnectionType>;
    multi?: boolean;
    onToggle?: () => void;
    draggable?: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    dropTarget?: boolean;
    onDropItem?: () => void;
}

export const File: FC<Props> = ({
    id,
    index,
    name,
    type,
    thumbnail,
    extension,
    selected,
    onClick,
    navUp,
    newFolder,
    hasChildren,
    connections,
    multi,
    onToggle,
    draggable,
    onDragStart,
    onDragEnd,
    dropTarget,
    onDropItem,
}) => {
    const depthRef = useRef(0);

    const [ over, setOver ] = useState(false);

    const classes = [ "file" ];

    if(selected) {
        classes.push("selected");
    }

    if(newFolder) {
        classes.push("new-folder");
    }

    if(dropTarget) {
        classes.push("drop-target");

        if(over) {
            classes.push("drop-over");
        }
    }

    let media;

    if(type === MediaItemType.Folder) {
        media = (
            <span className="fa-stack">
                <FontAwesomeLight icon="folder" className="fa-stack-1x" />
                {navUp && (
                    <FontAwesomeLight className="sub-icon fa-stack-1x" icon="level-up" />
                )}
                {newFolder && (
                    <FontAwesomeLight className="sub-icon fa-stack-1x" icon="plus" />
                )}
                {hasChildren && (
                    <FontAwesomeLight className="sub-icon fa-stack-1x" icon="file-image" />
                )}
            </span>
        );
    } else {
        let icon = null;

        const type = getFileType(extension);

        if(type === MediaType.Video) {
            icon = <FontAwesomeLight icon="file-video" />;
        } else if(type === MediaType.Image) {
            icon = <FontAwesomeLight icon="file-image" />;
        }

        media = (
            <Fragment>
                <img
                    alt={`Thumbnail for ${thumbnail}`}
                    src={thumbnail}
                    className="media-item"
                    // Prevent the browser's default image drag
                    draggable={false}
                />
                <div className="media-type">
                    {icon}
                </div>
                {(multi && typeof onToggle === "function") && (
                    <button
                        type="button"
                        className="media-multi-checkbox"
                        onClick={e => {
                            if(typeof onToggle !== "function") {
                                return;
                            }

                            e.stopPropagation();
                            onToggle();
                        }}
                    >
                        <FontAwesomeSolid icon={selected ? "check-square" : "square"} />
                    </button>
                )}
            </Fragment>
        );
    }

    const onDragEnter = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();

        // Count enter/leave pairs, since moving across a child fires both
        depthRef.current++;

        setOver(true);
    };

    const onDragOver = (e: DragEvent<HTMLDivElement>): void => {
        // Marks this tile as a valid drop target
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDragLeave = (): void => {
        depthRef.current = Math.max(0, depthRef.current - 1);

        if(depthRef.current === 0) {
            setOver(false);
        }
    };

    const onDrop = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        depthRef.current = 0;

        setOver(false);

        onDropItem?.();
    };

    return (
        <div
            className={classes.join(" ")}
            draggable={draggable}
            onDragStart={draggable ? (e => {
                // Firefox won't start a drag unless some data is set on the transfer
                e.dataTransfer.setData(MEDIA_DRAG_MIME, id);
                e.dataTransfer.effectAllowed = "move";

                onDragStart?.();
            }) : undefined}
            onDragEnd={draggable ? onDragEnd : undefined}
            onDragEnter={dropTarget ? onDragEnter : undefined}
            onDragOver={dropTarget ? onDragOver : undefined}
            onDragLeave={dropTarget ? onDragLeave : undefined}
            onDrop={dropTarget ? onDrop : undefined}
            onClick={e => {
                onClick(e, index, {
                    type,
                    id,
                    name,
                    index,
                });
            }}
        >
            <div className={`media-thumbnail ${type}-item`}>
                {media}
            </div>
            <div className="media-details">
                {connections.length > 0 && (
                    <span className="inline-block mr-1">
                        <FontAwesomeLight icon="paperclip" />
                    </span>
                )}
                {name}
            </div>
        </div>
    );
};
